/**
 * Who a crosstab counts, said in words and chosen on purpose (M4S-5777 X20, X22).
 *
 * The rule itself has one home: the portal's `responded_id` codes in `constants.ts`
 * (`COMPLETE_RESPONDED_ID`, `TERMINATE_RESPONDED_ID`), bound by the pivot, never a literal
 * (`who-counts.guard.test.ts`). This module is the one home of
 *
 *  - **the data sets a crosstab may be built on** (X22). `completes` is the default and the only
 *    set that is ever mixed with nothing. `terminates` (screened out) is offered because the portal
 *    keeps their answers up to the point they were screened out (checked read-only on 2026-10-17:
 *    every terminate on six of seven recent surveys has answer rows). Partials are NOT offered: a
 *    respondent who started and did not finish is `responded_id = 0` with no answer rows at all,
 *    so there is nothing to tabulate; that is a pipeline ask (docs/pdlc/crosstabs-one-engine.md).
 *    A set is never a mix: one crosstab reads one set.
 *  - **the sentence that says who counts** (`whoCountsText`), printed on the table and in every
 *    file through `provenance.ts`, with the people-not-entries clause of a diary study
 *    (`respondent-unit.ts`) and what the load left out (`exclusionsText`).
 *
 * Pure.
 */

import { COMPLETE_RESPONDED_ID, TERMINATE_RESPONDED_ID } from './constants';
import { formatCount } from './number-format';

/** The data sets a crosstab can be built on. */
export const RESPONDENT_SETS = ['completes', 'terminates'] as const;
export type RespondentSet = (typeof RESPONDENT_SETS)[number];

/** The set every crosstab is on unless someone chose another. */
export const DEFAULT_RESPONDENT_SET: RespondentSet = 'completes';

interface SetInfo {
    /** The portal code the pivot binds. */
    respondedId: number;
    /** The name in the picker and on the warning chip. */
    label: string;
    /** Who the set is, as the sentence on every table and file says it. */
    who: string;
}

const SETS: Record<RespondentSet, SetInfo> = {
    completes: {
        respondedId: COMPLETE_RESPONDED_ID,
        label: 'Completes',
        who: 'Completes only',
    },
    terminates: {
        respondedId: TERMINATE_RESPONDED_ID,
        label: 'Screened out (terminates)',
        who: 'Screened-out respondents (terminates) only, not completes; their answers stop where they were screened out',
    },
};

/** A set from anything (a URL parameter, a record, a request body), else null. */
export function parseRespondentSet(raw: unknown): RespondentSet | null {
    return typeof raw === 'string' && (RESPONDENT_SETS as readonly string[]).includes(raw)
        ? (raw as RespondentSet)
        : null;
}

/** A set as stored: `completes`, absent or anything unknown reads as the default. */
export function respondentSetOf(raw: unknown): RespondentSet {
    return parseRespondentSet(raw) ?? DEFAULT_RESPONDENT_SET;
}

/** The portal `responded_id` the pivot binds for a set. */
export function respondedIdOf(set: RespondentSet): number {
    return SETS[set].respondedId;
}

/** The set's name, for the picker and the chip. */
export function respondentSetLabel(set: RespondentSet): string {
    return SETS[set].label;
}

/** True for every set but the default: the table then says, loudly, that it is not completes. */
export function isNonDefaultSet(set: RespondentSet): boolean {
    return set !== DEFAULT_RESPONDENT_SET;
}

/**
 * Respondents per portal status in the loaded surveys, counted as people per survey (the unit a
 * crosstab counts). Keys are the portal's codes as strings; `0` (invited, never responded) is not
 * carried.
 */
export type StatusCounts = Record<string, number>;

const STATUS_NAMES: Record<string, string> = {
    '1': 'completes',
    '2': 'screened out (terminates)',
    '3': 'retired (completes removed in quality checks, expired or removed invitations)',
    '4': 'completes hidden from delivery',
};

/**
 * What the load left out, as a clause: "Not counted: 12 screened out (terminates), 734 retired
 * (…)". Null when nothing was counted or nothing was left out. The set the crosstab is on is never
 * listed.
 */
export function exclusionsText(
    counts: StatusCounts | null | undefined,
    set: RespondentSet = DEFAULT_RESPONDENT_SET,
): string | null {
    if (!counts) return null;
    const own = String(respondedIdOf(set));
    const parts: string[] = [];
    for (const code of ['1', '2', '3', '4']) {
        if (code === own) continue;
        const n = Number(counts[code] ?? 0);
        if (Number.isFinite(n) && n > 0) parts.push(`${formatCount(n)} ${STATUS_NAMES[code]}`);
    }
    return parts.length ? `Not counted: ${parts.join('; ')}.` : null;
}

/**
 * The one sentence every table and file states about who counts (X20): the set, and on a diary
 * or other multi-entry study whether it counts people or entries (`unitNote`, from
 * `respondentUnitNote`). Exclusions are a separate clause (`exclusionsText`).
 */
export function whoCountsText(set: RespondentSet, unitNote?: string | null): string {
    const base =
        set === DEFAULT_RESPONDENT_SET
            ? `${SETS[set].who}. Respondents removed in quality checks (retired), screened out or hidden from delivery are not counted.`
            : `${SETS[set].who}.`;
    return unitNote ? `${base} ${unitNote}` : base;
}

/** The short form for a chip on the table: "Completes" or "Screened out (terminates)". */
export function whoCountsShort(set: RespondentSet, unitNoun?: string | null): string {
    const label = respondentSetLabel(set);
    return unitNoun ? `${label} · ${unitNoun}` : label;
}
